import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { PageHeader } from '@/components/layout/PageHeader';
import { QueryBoundary } from '@/components/query-boundary';
import { Button } from '@/components/ui/button';
import { VisitorMemoryDetail } from '@/components/memory/VisitorMemoryDetail';
import { useMemoryVisitor, useInvalidateFact, useDeleteVisitorMemory } from '@/lib/queries/memory';
import { useConfirm } from '@/components/confirm';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';

export const Route = createFileRoute('/memory/$visitorId')({ component: MemoryDetailPage });

function MemoryDetailPage() {
  const { visitorId } = Route.useParams();
  const q = useMemoryVisitor(visitorId);
  const invalidate = useInvalidateFact(visitorId);
  const delAll = useDeleteVisitorMemory();
  const confirm = useConfirm();
  const navigate = useNavigate();

  async function onInvalidate(factId: number) {
    const ok = await confirm({
      title: '기억 무효화',
      description: 'recall 에서 즉시 제외되고 30일 뒤 물리삭제됩니다.',
      confirmLabel: '무효화',
      tone: 'destructive',
    });
    if (!ok) return;
    try { await invalidate.mutateAsync(factId); toast.success('무효화됨'); }
    catch (e) { toast.error((e as Error).message); }
  }

  async function onDeleteAll() {
    const ok = await confirm({
      title: '기억 전체 삭제',
      description: '이 방문자의 개체·사실이 모두 삭제됩니다. 미처리 세션은 재추출되지 않게 마킹됩니다.',
      confirmLabel: '전체 삭제',
      tone: 'destructive',
    });
    if (!ok) return;
    try {
      await delAll.mutateAsync(visitorId);
      toast.success('삭제됨');
      void navigate({ to: '/memory', search: { page: 1 } });
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <>
      <PageHeader
        title="방문자 기억"
        description={visitorId}
        actions={
          <Button variant="destructive" size="sm" disabled={delAll.isPending} onClick={onDeleteAll}>
            <Trash2 /> 기억 전체 삭제
          </Button>
        }
      />
      <QueryBoundary query={q}>
        {detail => (
          <VisitorMemoryDetail detail={detail} onInvalidate={onInvalidate} invalidating={invalidate.isPending} />
        )}
      </QueryBoundary>
    </>
  );
}
